import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { Tools } from "@/@corefabriccanvas";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CropIcon,
  DotIcon,
  EraserIcon,
  HandIcon,
  MarkerIcon,
  PenIcon,
  SelectIcon,
  ShapesIcon,
  TrashIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "@/icons";
import PenSubTool from "./PenSubTool";
import EraserSubTool from "./EraserSubTool";
import ShapesSubTool from "./ShapeSubTool";
import { User } from "@/database/userDatabaseService";
import { DisplayTypes, SubToolType } from "@/types";
import "./index.scss";
import { useTranslation } from "react-i18next";

interface Tool {
  tool: Tools;
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  isActive: boolean;
  disabled?: boolean;
  subToolType?: SubToolType;
  subTool?: React.ReactNode;
}

interface ToolBoxProps {
  displayType: string;
  currentPageItem: any;
  user: User;
  currentTool: Tools;
  selectedColor: string;
  previousTool: Tools;
  pageItemPosition: string;
  dispatch: (action: any) => void;
  screenWidth: number;
  setTool: (tool: Tools) => void;
  canvas: any;
  setSelectedColor: (color: string, userId: string) => void;
  thickness: number;
  setThickness: (thickness: number, userId: string) => void;
  eraseThickness: number;
  setEraserThickness: (thickness: number, userId: string) => void;
  updateActiveSubTool: (subTool: SubToolType | undefined) => void;
  activeSubTool: SubToolType | undefined;
}

const ToolBox: React.FC<ToolBoxProps> = ({
  displayType,
  currentPageItem,
  user,
  currentTool,
  previousTool,
  pageItemPosition,
  dispatch,
  screenWidth,
  setTool,
  canvas,
  selectedColor,
  setSelectedColor,
  thickness,
  setThickness,
  eraseThickness,
  setEraserThickness,
  updateActiveSubTool,
  activeSubTool,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "tools" });

  const deviceRatio = window.devicePixelRatio;
  const [isSimpleMode, setIsSimpleMode] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 100,
    y: 100 / deviceRatio,
  });
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [toolBoxPosition, setToolBoxPosition] = useState<"left" | "right">(
    "left"
  );
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const nodeRef = React.useRef<HTMLDivElement>(document.createElement("div"));

  useEffect(() => {});

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  const zoom = (type: "zoomin" | "zoomout") => {
    if (canvas) {
      canvas.zoomInOut(type);
    }
  };

  const tools: Tool[] = [
    {
      tool: Tools.Pan,
      icon: <HandIcon className={undefined} />,
      title: "move",
      onClick: () => setTool(Tools.Pan),
      isActive: currentTool === Tools.Pan,
    },
    {
      tool: Tools.Select,
      icon: <SelectIcon className={undefined} />,
      title: "select",
      onClick: () => setTool(Tools.Select),
      isActive: currentTool === Tools.Select,
    },
    {
      tool: Tools.Pencil,
      icon: <PenIcon className={undefined} />,
      title: "pen",
      isActive: currentTool === Tools.Pencil,
      onClick: () => {
        setTool(Tools.Pencil);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Pen ? SubToolType.Pen : undefined
        );
      },
      subToolType: SubToolType.Pen,
      subTool: (
        <PenSubTool
          selectedColor={selectedColor}
          selectedThickness={thickness}
          user={user}
          setSelectedColor={setSelectedColor}
          setThickness={setThickness}
          subtoolPosition={toolBoxPosition}
        />
      ),
    },
    {
      tool: Tools.Marker,
      icon: <MarkerIcon className={undefined} />,
      title: "marker",
      isActive: currentTool === Tools.Marker,
      onClick: () => {
        setTool(Tools.Marker);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Marker ? SubToolType.Marker : undefined
        );
      },
      subToolType: SubToolType.Marker,
      subTool: (
        <PenSubTool
          selectedColor={selectedColor}
          selectedThickness={thickness}
          user={user}
          setSelectedColor={setSelectedColor}
          subtoolPosition={toolBoxPosition}
          setThickness={setThickness}
        />
      ),
    },
    {
      tool: Tools.Eraser,
      icon: <EraserIcon className={undefined} />,
      title: "eraser",
      isActive: currentTool === Tools.Eraser,
      onClick: () => {
        setTool(Tools.Eraser);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Eraser ? SubToolType.Eraser : undefined
        );
      },
      subToolType: SubToolType.Eraser,
      subTool: (
        <EraserSubTool
          user={user}
          thickness={eraseThickness}
          previousTool={previousTool}
          dispatch={dispatch}
          setThickness={setEraserThickness}
          subToolPosition={toolBoxPosition}
          setTool={setTool}
          canvas={canvas}
        />
      ),
    },
    {
      tool: Tools.Crop,
      icon: <CropIcon className={undefined} />,
      title: "cut",
      isActive: currentTool === Tools.Crop,
      disabled:
        displayType === DisplayTypes.BlankPage ||
        displayType === DisplayTypes.PageItem,
      onClick: () => setTool(Tools.Crop),
    },
    {
      tool: Tools.Shapes,
      icon: <ShapesIcon className={undefined} />,
      isActive:
        currentTool === Tools.Line ||
        currentTool === Tools.DashedLine ||
        currentTool === Tools.Arrow ||
        currentTool === Tools.Circle ||
        currentTool === Tools.Polygon ||
        currentTool === Tools.Rectangle ||
        currentTool === Tools.Triangle ||
        currentTool === Tools.Text,
      title: "shapes",
      onClick: () => {
        updateActiveSubTool(
          activeSubTool !== SubToolType.Shapes ? SubToolType.Shapes : undefined
        );
      },
      subToolType: SubToolType.Shapes,
      subTool: (
        <ShapesSubTool setTool={setTool} subtoolPosition={toolBoxPosition} />
      ),
    },
    {
      tool: Tools.Trash,
      icon: <TrashIcon className={undefined} color={undefined} />,
      title: "clear_drawings",
      onClick: () => canvas.clear(),
      isActive: false,
    },
    {
      tool: Tools.ZoomIn,
      icon: <ZoomInIcon className={undefined} />,
      title: "zoom_in",
      onClick: () => zoom("zoomin"),
      isActive: false,
    },
    {
      tool: Tools.ZoomOut,
      icon: <ZoomOutIcon className={undefined} />,
      title: "zoom_out",
      onClick: () => zoom("zoomout"),
      isActive: false,
    },
  ];

  useEffect(() => {
    const setToolPosition = () => {
      if (
        displayType === DisplayTypes.PageItem &&
        canvas &&
        canvas._fc.backgroundImage.lineCoords
      ) {
        setIsAnimating(true);
        const startx = canvas._fc.backgroundImage.lineCoords.bl.x;
        const endx = canvas._fc.backgroundImage.lineCoords.br.x;
        if (
          ((position.x >= startx && position.x <= endx) ||
            position.x <= startx) &&
          pageItemPosition === "left"
        ) {
          setPosition((prev) => ({ x: screenWidth - 250, y: prev.y }));
        }
        if (
          ((position.x >= startx && position.x <= endx) ||
            position.x >= startx) &&
          pageItemPosition === "right"
        ) {
          setPosition((prev) => ({ x: 50, y: prev.y }));
        }
        setTimeout(() => {
          setIsAnimating(false);
        }, 600);
      }
    };
    setTimeout(() => {
      if (user.userSettings.isToolsAutoMoving === true) {
        setToolPosition();
      }
    }, 500);
  }, [
    displayType,
    currentPageItem,
    canvas,
    pageItemPosition,
    position.x,
    screenWidth,
    user.userSettings.isToolsAutoMoving,
  ]);

  useEffect(() => {
    setToolBoxPosition(position.x >= screenWidth / 2 ? "left" : "right");
  }, [position, screenWidth]);

  return (
    !isMobile && (
      <Draggable
        nodeRef={nodeRef}
        cancel=".tools-area"
        onDrag={(e, data) => {
          setPosition({ x: data.x, y: data.y });
        }}
        position={position}
      >
        <div
          className={`tool-box ${isAnimating ? "animate" : ""}`}
          ref={nodeRef}
          style={{
            position: "absolute",
            top: `${100 / deviceRatio}px`,
            left: "100px",
          }}
        >
          <div className="move-area">
            <DotIcon className={undefined} />
            <DotIcon className={undefined} />
            <DotIcon className={undefined} />
          </div>
          <div className={`tools-area ${toolBoxPosition}`}>
            {tools.map((tool, index) => {
              if (
                (isSimpleMode &&
                  user.userSettings?.selectedTools.includes(tool.tool)) ||
                !isSimpleMode
              ) {
                return (
                  <div
                    key={`tool-${index}`}
                    onClick={tool.onClick}
                    className={tool.isActive ? `tool-btn active` : "tool-btn"}
                  >
                    {tool.icon}
                    {tool.subToolType &&
                      activeSubTool === tool.subToolType &&
                      tool.subTool}
                    <span>{t(tool.title)}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
          <div
            className="tool-box-footer tools-area"
            onClick={() => setIsSimpleMode(!isSimpleMode)}
          >
            {isSimpleMode ? (
              <>
                <span>{t("expand")}</span>
                <ArrowDownIcon className={undefined} />
              </>
            ) : (
              <>
                <span>{t("collapse")}</span>
                <ArrowUpIcon className={undefined} />
              </>
            )}
          </div>
        </div>
      </Draggable>
    )
  );
};

export default React.memo(ToolBox);
